import { CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Flex,
  FormLabel,
  IconButton,
  Image,
  Input,
  List,
  ListItem,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import debounce from "debounce";
import Downshift from "downshift";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  attachAdminScreenRelease,
  detachAdminScreenRelease,
  getAdminReadingOrderScreenReleases,
  getAdminScreenReleases,
} from "../../../services/adminApi";

const FRANCHISE_LABELS = {
  mcu: "MCU",
  dcu: "DCU",
  dceu: "DCEU",
  star_wars: "Star Wars",
};

/**
 * "Film · 2026" or "Season 2 · 2026"
 */
export const formatReleaseMeta = (release) => {
  const kind =
    release?.type === "movie"
      ? "Film"
      : `Season ${Number(release?.seasonNumber) || 1}`;
  const year = release?.releaseDate
    ? String(release.releaseDate).slice(0, 4)
    : "";
  const franchise = FRANCHISE_LABELS[release?.franchise] || "";

  return [kind, year, franchise].filter(Boolean).join(" · ");
};

const itemToString = (item) => item?.title || "";

const posterStyle = { width: "30px", height: "45px", marginRight: "10px" };

/**
 * Admin picker linking movies / show seasons (screen releases) to a
 * Cinematic Universe reading order.
 *
 * With a readingOrderId, links are saved immediately through the API.
 * Without one (new reading order), the selection is kept in `value` and the
 * caller links it once the order exists.
 */
const ScreenReleaseSelector = ({ readingOrderId, value = [], onChange }) => {
  const toast = useToast();
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Load what is already linked when editing an existing reading order.
  useEffect(() => {
    if (!readingOrderId) {
      return;
    }
    let cancelled = false;
    getAdminReadingOrderScreenReleases(readingOrderId)
      .then((response) => {
        if (!cancelled) {
          onChange(response?.data || []);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readingOrderId]);

  const fetchSuggestions = useCallback((query) => {
    setLoading(true);
    getAdminScreenReleases(query)
      .then((response) => {
        setSuggestions(response?.data || []);
        setMenuOpen(true);
      })
      .catch(() => setSuggestions([]))
      .finally(() => setLoading(false));
  }, []);

  const debouncedFetch = useMemo(
    () => debounce(fetchSuggestions, 400),
    [fetchSuggestions]
  );

  useEffect(() => {
    if (!inputValue.trim()) {
      setSuggestions([]);
      setMenuOpen(false);
      return;
    }
    debouncedFetch(inputValue.trim());
    return () => debouncedFetch.clear();
  }, [inputValue, debouncedFetch]);

  const notifyError = (title, error) =>
    toast({
      title,
      description: error?.response?.data?.message || error?.message,
      status: "error",
      duration: 5000,
      isClosable: true,
    });

  const addRelease = (release) => {
    if (!release || value.some((item) => item.id === release.id)) {
      return;
    }
    if (!readingOrderId) {
      onChange([...value, release]);
      return;
    }
    setSaving(true);
    attachAdminScreenRelease(readingOrderId, release.id)
      .then((response) => onChange(response?.data || [...value, release]))
      .catch((error) => notifyError("Unable to link this release", error))
      .finally(() => setSaving(false));
  };

  const removeRelease = (release) => {
    if (!readingOrderId) {
      onChange(value.filter((item) => item.id !== release.id));
      return;
    }
    setSaving(true);
    detachAdminScreenRelease(readingOrderId, release.id)
      .then((response) =>
        onChange(
          response?.data || value.filter((item) => item.id !== release.id)
        )
      )
      .catch((error) => notifyError("Unable to unlink this release", error))
      .finally(() => setSaving(false));
  };

  return (
    <Box w="100%">
      <FormLabel>Movies &amp; series</FormLabel>
      <Text fontSize="sm" color="gray.400" mb="2">
        Link the releases this reading order prepares for. They show a "Reading
        order available" badge in the apps.
      </Text>

      {value.length > 0 && (
        <List spacing="1" mb="2">
          {value.map((release) => (
            <ListItem
              key={release.id}
              bg="gray.800"
              borderRadius="md"
              p="2"
              display="flex"
              alignItems="center"
            >
              {release.posterUrl && (
                <Image
                  src={release.posterUrl}
                  alt={release.title}
                  style={posterStyle}
                />
              )}
              <Box flex="1">
                <Text>{release.title}</Text>
                <Text fontSize="sm" color="gray.400">
                  {formatReleaseMeta(release)}
                </Text>
              </Box>
              <IconButton
                aria-label={`Remove ${release.title}`}
                icon={<CloseIcon boxSize="3" />}
                size="sm"
                variant="ghost"
                isDisabled={saving}
                onClick={() => removeRelease(release)}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Downshift
        inputValue={inputValue}
        onInputValueChange={(nextValue) => setInputValue(nextValue ?? "")}
        itemToString={itemToString}
        selectedItem={null}
        isOpen={menuOpen}
        onOuterClick={() => setMenuOpen(false)}
        onChange={(release) => {
          addRelease(release);
          setInputValue("");
          setMenuOpen(false);
        }}
      >
        {({ getInputProps, getMenuProps, getItemProps, highlightedIndex }) => (
          <div style={{ position: "relative", width: "100%" }}>
            <Input
              {...getInputProps({
                placeholder: "Search a movie or series by title...",
                "aria-label": "Search a movie or series",
                isDisabled: saving,
                onFocus: () => suggestions.length && setMenuOpen(true),
              })}
            />
            {menuOpen && (
              <List
                position="absolute"
                borderRadius="lg"
                top="45"
                left="0"
                w="100%"
                zIndex="dropdown"
                {...getMenuProps({
                  style: { maxHeight: 300, overflowY: "auto" },
                })}
              >
                {loading && (
                  <ListItem>
                    <Box bg="gray.900" p="2">
                      <Spinner size="sm" />
                    </Box>
                  </ListItem>
                )}
                {!loading && suggestions.length === 0 && (
                  <ListItem>
                    <Box bg="gray.900" color="white" p="2">
                      <Text>
                        No release found for <strong>{inputValue}</strong>
                      </Text>
                    </Box>
                  </ListItem>
                )}
                {!loading &&
                  suggestions.map((item, index) => (
                    <ListItem {...getItemProps({ item, key: item.id, index })}>
                      <Flex
                        bg={
                          highlightedIndex === index ? "blue.500" : "gray.900"
                        }
                        color="white"
                        alignItems="center"
                        p="2"
                        cursor="pointer"
                        _hover={{ bg: "gray.600" }}
                      >
                        {item.posterUrl && (
                          <Image
                            src={item.posterUrl}
                            alt={item.title}
                            style={posterStyle}
                          />
                        )}
                        <Box>
                          <Text>{item.title}</Text>
                          <Text fontSize="sm" color="gray.300">
                            {formatReleaseMeta(item)}
                          </Text>
                        </Box>
                      </Flex>
                    </ListItem>
                  ))}
              </List>
            )}
          </div>
        )}
      </Downshift>
    </Box>
  );
};

export default ScreenReleaseSelector;
